import React, { useState } from 'react';
import { Container, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useGetAllEmailTemplate } from '../services/react-query/email_templates';
import { Loading } from '../uikit';
import { EmailTemplate } from '../types/EmailTemplate';
import AppLink from '../components/common/AppLink/AppLink';
import { ROUTE_PATHS } from '../routes/Routes';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { UserConfigurableModule } from '../types/User';
import { archiveFilter } from '../components/common/filters/consts';
import { FilterState, IFilter } from '../components/common/filters/interfaces';
import { defaultContainerStyling, listPageLayoutStyles, listPageTableRowStyling, listPageTableWrapperStyles } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import PaginationBar from '../components/PaginationBar';

const EmailTemplatesList: React.FC = () => {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { data, isLoading } = useGetAllEmailTemplate({
    params: {
      page: currentPage,
      search: useSearchTerm(),
      filters: filterState,
    },
    enabled: true,
  });

  if (isLoading) return <Loading />;

  const templates: EmailTemplate[] = data?.data || [];
  const meta = data?.meta;
  const totalPages = Number(meta?.total_pages);
  const moduleFilters: IFilter[] = [archiveFilter];

  return (
    <Container maxWidth="xl">
      <ListHeader
        title="Email Templates"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={moduleFilters}
            module={UserConfigurableModule.EMAIL_TEMPLATES}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <TableContainer sx={defaultContainerStyling}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.uuid} sx={{ ...listPageTableRowStyling }}>
                    <TableCell>
                      <AppLink to={`/${ROUTE_PATHS.EMAIL_TEMPLATES}/${template.uuid}`}>{template.name}</AppLink>
                    </TableCell>
                    <TableCell>{template.subject}</TableCell>
                    <TableCell>{template.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <PaginationBar page={currentPage} count={totalPages} onChange={(_e, v) => setCurrentPage(v)} />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default EmailTemplatesList;
